import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Preloader from '../components/Preloader';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DonationConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      // No session ID — user didn't come from a donation
      navigate('/donate', { replace: true });
      return;
    }

    const fetchSession = async () => {
      try {
        const response = await fetch(`/api/checkout-session/${sessionId}`);

        if (!response.ok) {
          throw new Error('Could not verify donation');
        }

        const data = await response.json();
        setSessionData(data);
      } catch (err) {
        console.error('Failed to load session:', err);
        setError('We could not verify your donation. If you completed a payment, please check your email for a receipt from Stripe.');
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    if (window.initPathioScripts) {
      window.initPathioScripts();
    }
  }, [searchParams, navigate]);

  const formatCurrency = (amountInCents) => {
    return (amountInCents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (unix) => {
    return new Date(unix * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Preloader />
        <Header />
        <div style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e0e0e0',
            borderTop: '4px solid var(--ul-primary, #2B7A8F)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <p style={{ color: '#666', fontSize: '16px' }}>Verifying your donation...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
        <Footer />
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <Preloader />
        <Sidebar />
        <Header />
        <main>
          <div style={{
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center',
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#FFF3CD',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '36px',
              marginBottom: '24px',
            }}>⚠️</div>
            <h2 style={{ marginBottom: '12px', color: '#333' }}>Something Went Wrong</h2>
            <p style={{ color: '#666', maxWidth: '500px', marginBottom: '24px', lineHeight: '1.6' }}>{error}</p>
            <Link
              to="/donate"
              style={{
                background: 'var(--ul-primary, #2B7A8F)',
                color: '#fff',
                padding: '14px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'opacity 0.2s',
              }}
            >
              Back to Donate
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Find donation vs fee line items
  const donationItem = sessionData.line_items?.find(item => item.description?.includes('Pathio')) || sessionData.line_items?.[0];
  const feeItem = sessionData.line_items?.find(item => item.description?.includes('Processing'));

  return (
    <>
      <Preloader />
      <Sidebar />
      <Header />

      <main>
        <style>{`
          .confirm-page {
            min-height: 60vh;
            padding: clamp(40px, 6vw, 80px) clamp(16px, 4vw, 20px);
            background: linear-gradient(180deg, #f0fafb 0%, #ffffff 40%);
          }
          .confirm-container {
            max-width: 680px;
            margin: 0 auto;
          }
          .confirm-hero {
            text-align: center;
            margin-bottom: clamp(32px, 5vw, 48px);
          }
          .confirm-checkmark {
            width: clamp(80px, 12vw, 100px);
            height: clamp(80px, 12vw, 100px);
            border-radius: 50%;
            background: linear-gradient(135deg, #2B7A8F 0%, #1a9e6d 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto clamp(20px, 3vw, 28px);
            box-shadow: 0 8px 32px rgba(43, 122, 143, 0.3);
            animation: scaleIn 0.5s ease-out;
          }
          .confirm-checkmark svg {
            width: 48px;
            height: 48px;
            stroke: #fff;
            stroke-width: 3;
            fill: none;
            animation: drawCheck 0.6s ease-out 0.3s both;
          }
          @keyframes scaleIn {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          @keyframes drawCheck {
            from { stroke-dasharray: 100; stroke-dashoffset: 100; }
            to { stroke-dasharray: 100; stroke-dashoffset: 0; }
          }
          .confirm-hero h1 {
            font-size: clamp(24px, 4vw, 34px);
            color: #1a1a1a;
            margin-bottom: 10px;
            font-weight: 700;
          }
          .confirm-hero p {
            font-size: clamp(15px, 2vw, 17px);
            color: #666;
            max-width: 480px;
            margin: 0 auto;
            line-height: 1.6;
          }
          .confirm-card {
            background: #fff;
            border-radius: 16px;
            box-shadow: 0 4px 24px rgba(0,0,0,0.06);
            padding: clamp(24px, 4vw, 40px);
            margin-bottom: clamp(20px, 3vw, 28px);
          }
          .confirm-amount-display {
            text-align: center;
            padding: clamp(20px, 3vw, 32px) 0;
            border-bottom: 1px solid #f0f0f0;
            margin-bottom: clamp(20px, 3vw, 28px);
          }
          .confirm-amount-label {
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #999;
            margin-bottom: 8px;
          }
          .confirm-amount-value {
            font-size: clamp(36px, 6vw, 52px);
            font-weight: 800;
            color: #2B7A8F;
            line-height: 1.1;
          }
          .confirm-detail-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 14px 0;
            border-bottom: 1px solid #f5f5f5;
          }
          .confirm-detail-row:last-child {
            border-bottom: none;
          }
          .confirm-detail-label {
            font-size: clamp(13px, 1.8vw, 15px);
            color: #888;
          }
          .confirm-detail-value {
            font-size: clamp(13px, 1.8vw, 15px);
            color: #333;
            font-weight: 600;
            text-align: right;
          }
          .confirm-status-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: #E8F8F0;
            color: #1a9e6d;
            padding: 5px 14px;
            border-radius: 20px;
            font-size: 13px;
            font-weight: 600;
          }
          .confirm-status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #1a9e6d;
          }
          .confirm-next-section {
            padding: clamp(24px, 4vw, 32px);
          }
          .confirm-next-section h3 {
            font-size: clamp(16px, 2.2vw, 19px);
            color: #1a1a1a;
            margin-bottom: 20px;
            font-weight: 700;
          }
          .confirm-next-item {
            display: flex;
            gap: clamp(12px, 2vw, 16px);
            padding: 16px 0;
            border-bottom: 1px solid #f5f5f5;
          }
          .confirm-next-item:last-child {
            border-bottom: none;
            padding-bottom: 0;
          }
          .confirm-next-icon {
            width: 44px;
            height: 44px;
            min-width: 44px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
          }
          .confirm-next-icon.email { background: #EBF5FF; }
          .confirm-next-icon.tax { background: #F0FAF0; }
          .confirm-next-icon.community { background: #FFF5EB; }
          .confirm-next-text h4 {
            font-size: clamp(14px, 1.8vw, 15px);
            color: #333;
            margin-bottom: 4px;
            font-weight: 600;
          }
          .confirm-next-text p {
            font-size: clamp(13px, 1.6vw, 14px);
            color: #888;
            line-height: 1.5;
            margin: 0;
          }
          .confirm-actions {
            display: flex;
            gap: 12px;
            justify-content: center;
            flex-wrap: wrap;
            margin-bottom: clamp(20px, 3vw, 28px);
          }
          .confirm-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 14px 28px;
            border-radius: 10px;
            font-size: 15px;
            font-weight: 600;
            text-decoration: none;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
          }
          .confirm-btn-primary {
            background: var(--ul-primary, #2B7A8F);
            color: #fff;
          }
          .confirm-btn-primary:hover {
            opacity: 0.9;
            transform: translateY(-1px);
            color: #fff !important;
          }
          .confirm-btn-outline {
            background: transparent;
            color: var(--ul-primary, #2B7A8F);
            border: 2px solid var(--ul-primary, #2B7A8F);
          }
          .confirm-btn-outline:hover {
            background: rgba(43, 122, 143, 0.05);
          }
          .confirm-share {
            text-align: center;
            padding: clamp(24px, 4vw, 32px);
            background: #f9fafb;
            border-radius: 16px;
          }
          .confirm-share h3 {
            font-size: clamp(16px, 2.2vw, 18px);
            color: #333;
            margin-bottom: 6px;
          }
          .confirm-share p {
            font-size: 14px;
            color: #888;
            margin-bottom: 16px;
          }
          .confirm-share-links {
            display: flex;
            gap: 10px;
            justify-content: center;
            flex-wrap: wrap;
          }
          .confirm-share-link {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 10px 18px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            text-decoration: none;
            transition: opacity 0.2s;
          }
          .confirm-share-link:hover { opacity: 0.85; }
          .confirm-share-link.twitter { background: #1DA1F2; color: #fff; }
          .confirm-share-link.instagram { background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888); color: #fff; }
          .confirm-share-link.linkedin { background: #0077B5; color: #fff; }
          @media screen and (max-width: 576px) {
            .confirm-actions {
              flex-direction: column;
            }
            .confirm-btn {
              justify-content: center;
            }
          }
        `}</style>

        <section className="confirm-page">
          <div className="confirm-container">

            {/* Hero */}
            <div className="confirm-hero">
              <div className="confirm-checkmark">
                <svg viewBox="0 0 48 48">
                  <polyline points="12,24 22,34 36,14" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1>Thank You for Your Generosity!</h1>
              <p>
                Your donation to Pathio has been received. You're helping us build
                mentorship pathways and improve health literacy for communities that need it most.
              </p>
              </div>

            {/* Donation Details Card */}
            <div className="confirm-card">
              <div className="confirm-amount-display">
                <div className="confirm-amount-label">Total Donated</div>
                <div className="confirm-amount-value">
                  {formatCurrency(sessionData.amount_total)}
                  </div>
                </div>

              <div style={{ padding: '0' }}>
                {sessionData.customer_name && (
                  <div className="confirm-detail-row">
                    <span className="confirm-detail-label">Donor</span>
                    <span className="confirm-detail-value">{sessionData.customer_name}</span>
                  </div>
                )}
                {sessionData.customer_email && (
                  <div className="confirm-detail-row">
                    <span className="confirm-detail-label">Email</span>
                    <span className="confirm-detail-value">{sessionData.customer_email}</span>
                  </div>
                )}
                {donationItem && (
                  <div className="confirm-detail-row">
                    <span className="confirm-detail-label">Donation</span>
                    <span className="confirm-detail-value">{formatCurrency(donationItem.amount_total)}</span>
                  </div>
                )}
                {feeItem && (
                  <div className="confirm-detail-row">
                    <span className="confirm-detail-label">Processing Fees</span>
                    <span className="confirm-detail-value">{formatCurrency(feeItem.amount_total)}</span>
                  </div>
                )}
                <div className="confirm-detail-row">
                  <span className="confirm-detail-label">Date</span>
                  <span className="confirm-detail-value">{formatDate(sessionData.created)}</span>
                </div>
                {sessionData.mode === 'subscription' && (
                  <div className="confirm-detail-row">
                    <span className="confirm-detail-label">Type</span>
                    <span className="confirm-detail-value" style={{ color: 'var(--ul-primary, #2B7A8F)', fontWeight: 700 }}>
                      Recurring Subscription
                    </span>
                  </div>
                )}
                <div className="confirm-detail-row">
                  <span className="confirm-detail-label">Status</span>
                  <span className="confirm-detail-value">
                    <span className="confirm-status-badge">
                      <span className="confirm-status-dot"></span>
                      {sessionData.mode === 'subscription' ? 'Active' : 'Paid'}
                    </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* What's Next */}
            <div className="confirm-card confirm-next-section">
              <h3>What Happens Next</h3>

              <div className="confirm-next-item">
                <div className="confirm-next-icon email">📧</div>
                <div className="confirm-next-text">
                  <h4>Receipt on the Way</h4>
                  <p>
                    A receipt will be sent to{' '}
                    <strong>{sessionData.customer_email || 'your email'}</strong>.
                    Keep it for your tax records.
                  </p>
                    </div>
                    </div>

              {sessionData.mode === 'subscription' && (
                <div className="confirm-next-item">
                  <div className="confirm-next-icon" style={{ background: '#EDE9FE' }}>🔄</div>
                  <div className="confirm-next-text">
                    <h4>Recurring Donation Active</h4>
                    <p>
                      Your card will be automatically charged {formatCurrency(sessionData.amount_total)} on
                      each billing cycle. You can cancel anytime by contacting us at{' '}
                      <strong>pathio.org@gmail.com</strong>.
                    </p>
                      </div>
                      </div>
              )}

              <div className="confirm-next-item">
                <div className="confirm-next-icon tax">📋</div>
                <div className="confirm-next-text">
                  <h4>Tax-Deductible</h4>
                  <p>
                    Pathio is a registered 501(c)(3) nonprofit. Your donation is
                    tax-deductible to the extent allowed by law.
                </p>
              </div>
              </div>

              <div className="confirm-next-item">
                <div className="confirm-next-icon community">🤝</div>
                <div className="confirm-next-text">
                  <h4>Your Impact</h4>
                  <p>
                    Your support helps fund peer mentorship, health education content,
                    and community resource connections for Central Asian communities.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="confirm-actions">
              <a
                href={`/api/receipt/${searchParams.get('session_id')}/download`}
                className="confirm-btn confirm-btn-primary"
                style={{ textDecoration: 'none' }}
              >
                📥 Download Receipt (PDF)
              </a>
              <Link to="/" className="confirm-btn confirm-btn-outline">
                Back to Home
              </Link>
              <Link to="/donate" className="confirm-btn confirm-btn-outline">
                Donate Again
              </Link>
            </div>

            {/* Share */}
            <div className="confirm-share">
              <h3>Spread the Word</h3>
              <p>Help others discover Pathio's mission</p>
              <div className="confirm-share-links">
                <a
                  href="https://twitter.com/intent/tweet?text=I%20just%20donated%20to%20%40pathio_org%20%E2%80%94%20a%20student-led%20nonprofit%20improving%20health%20literacy%20and%20mentorship%20for%20Central%20Asian%20communities.%20Consider%20supporting%20them!%20https%3A%2F%2Fpathio.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="confirm-share-link twitter"
                >
                  𝕏 Post
                </a>
                <a
                  href="https://www.instagram.com/pathio_org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="confirm-share-link instagram"
                >
                  Instagram
                </a>
                <a
                  href="https://www.linkedin.com/sharing/share-offsite/?url=https://pathio.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="confirm-share-link linkedin"
                >
                  LinkedIn
                </a>
            </div>
          </div>

          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default DonationConfirmation;
